import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { SUPABASE_ANON_KEY, SUPABASE_FUNCTIONS_URL } from "@/lib/supabase";
import { useAssignableUsers } from "@/hooks/use-users";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { hasMinRole } from "@/lib/constants";

interface Props {
  /** Forhåndsutfylt navn (typisk fra Monday-item/-gruppe). */
  defaultName: string;
  /**
   * Kalles etter at internprosjektet er opprettet i Supabase.
   * `projectId` brukes til å oppdatere import-preview-state.
   */
  onCreated: (projectId: string, projectCode: string) => void;
  onCancel: () => void;
}

interface CreateProjectResponse {
  project_id: string;
  project_code: string;
  error?: string;
}

/**
 * VIB-724a: Inline-form i Monday-import-flyten for å opprette et nytt
 * internprosjekt uten å forlate import-siden. Brukes på rader hvor parsed
 * project_code ikke matcher noe eksisterende Sølvrev-prosjekt.
 *
 * Edge function `create-project` tildeler INT##-kode automatisk når
 * `is_internal=true`. Owners må sendes (validering i edge function), så vi
 * bruker valgt PL som eneste eier med 100% ownership.
 */
export function CreateInternalProjectInlineForm({
  defaultName,
  onCreated,
  onCancel,
}: Props) {
  const { data: users = [] } = useAssignableUsers();
  const { activeUser } = useImpersonation();

  const adminUsers = useMemo(
    () => users.filter((u) => hasMinRole(u.role, "admin")),
    [users],
  );

  const [name, setName] = useState(defaultName);
  const defaultPlId = useMemo(() => {
    if (activeUser && hasMinRole(activeUser.role, "admin")) return activeUser.id;
    return adminUsers[0]?.id ?? "";
  }, [activeUser, adminUsers]);
  const [productionLeadId, setProductionLeadId] = useState<string>(defaultPlId);
  const [submitting, setSubmitting] = useState(false);

  // Når useUsers laster ferdig får vi gyldig default-PL. Oppdater bare hvis
  // brukeren ikke allerede har gjort et eget valg.
  useEffect(() => {
    if (!productionLeadId && defaultPlId) {
      setProductionLeadId(defaultPlId);
    }
  }, [defaultPlId, productionLeadId]);

  const canSubmit =
    name.trim().length > 0 && productionLeadId.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-project`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          name: name.trim(),
          is_internal: true,
          production_lead_id: productionLeadId,
          // Edge function krever minst én owner — for internprosjekter
          // bruker vi PL som eneste eier med 100%. Salgsenhet er irrelevant
          // når det ikke finnes en kunde å fakturere mot.
          owners: [{ user_id: productionLeadId, ownership_pct: 100 }],
          total_amount: 0,
          project_status: "kommende",
          payment_due_days: 14,
        }),
      });
      const data = (await res.json()) as CreateProjectResponse;
      if (!res.ok || data.error) {
        throw new Error(data.error ?? `create-project (${res.status})`);
      }
      toast.success(`Internprosjekt opprettet (${data.project_code})`);
      onCreated(data.project_id, data.project_code);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Kunne ikke opprette internprosjekt: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 p-2 bg-blue-50/40 border border-blue-200 rounded"
      data-testid="create-internal-project-form"
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Internprosjekt-navn"
        className="h-8 text-xs flex-1 min-w-[180px]"
        aria-label="Internprosjekt-navn"
      />
      <Select
        value={productionLeadId}
        onValueChange={setProductionLeadId}
      >
        <SelectTrigger
          className="h-8 text-xs w-[180px]"
          aria-label="Prosjektleder"
        >
          <SelectValue placeholder="Velg PL" />
        </SelectTrigger>
        <SelectContent>
          {adminUsers.length === 0 ? (
            <SelectItem value="__none__" disabled>
              Ingen admin-brukere funnet
            </SelectItem>
          ) : (
            adminUsers.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.full_name}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="h-8"
      >
        {submitting ? (
          <SrIcon name="loader2" className="w-3 h-3 animate-spin" />
        ) : (
          "Opprett"
        )}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onCancel}
        disabled={submitting}
        className="h-8"
      >
        Avbryt
      </Button>
    </div>
  );
}
